import {PhoneHQButton, PhoneHQDatepicker, PhoneHQSelect} from 'ch3-ui-lib';
import {Calendar, Download} from "lucide-react";
import moment from "moment-timezone";
import {ButtonHTMLAttributes, forwardRef, ReactNode, useState} from "react";
import {useGenerateBillingReportMutation} from "../../store/features/Billing/BillingService";
import AdminPanelRestrictedView from "../../components/AdminPanelRestrictedView/AdminPanelRestrictedView";
import {useSelector} from "react-redux";
import {getFormattedDate} from "../../utilis/timeUtilis";
import useMediaQuery from "../../utilis/screenWidthUtils.ts";
import type {RootState} from "../../store/store";

type ReportType = 'BILLING_COSTS' | 'INFOLINE_QUEUES' | 'INFOLINE_PHONE_NUMBERS' | 'INFOLINE_AGENTS';

interface DateRange {
    from: Date;
    to: Date;
    /** Preset name, e.g. "All time", shown instead of the dates. */
    label?: string;
}

const USAGE_REPORTS: { type: ReportType; title: string; filePrefix: string; phqId: string }[] = [
    {type: 'INFOLINE_QUEUES', title: 'Call Queue Report', filePrefix: 'CallQueue', phqId: 'reports-downloadCallQueueBtn'},
    {type: 'INFOLINE_PHONE_NUMBERS', title: 'Phone number Report', filePrefix: 'PhoneNumbers', phqId: 'reports-downloadPhoneNumberBtn'},
    {type: 'INFOLINE_AGENTS', title: 'Agent Report', filePrefix: 'AgentsReport', phqId: 'reports-downloadAgentBtn'},
];

/** The last 24 months, newest first, for the billing report. */
const MONTH_OPTIONS = Array.from({length: 24}, (_, i) => {
    const m = moment().startOf('month').subtract(i, 'months');
    return {value: m.format('YYYY-MM'), label: m.format('MMMM YYYY')};
});

const saveFile = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const BillingReport = () => {
    const role = useSelector((state: RootState) => state.auth.role);
    const isMobile = useMediaQuery('(max-width: 960px)');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [generateBillingReport] = useGenerateBillingReportMutation();

    const [month, setMonth] = useState(() => moment().format('YYYY-MM'));
    // One range per report: picking dates for one must not change the others. Each starts on this month.
    const [ranges, setRanges] = useState<Partial<Record<ReportType, DateRange>>>(() => {
        const thisMonth = {from: moment().startOf('month').toDate(), to: moment().endOf('month').toDate(), label: 'This month'};
        return Object.fromEntries(USAGE_REPORTS.map(r => [r.type, thisMonth]));
    });

    const downloadBilling = async () => {
        const m = moment(month, 'YYYY-MM');
        const dateFrom = m.clone().startOf('month').toDate().toISOString();
        const dateTo = m.clone().endOf('month').toDate().toISOString();
        const url = await generateBillingReport({dateFrom, dateTo, timezone, type: 'BILLING_COSTS'}).unwrap();
        saveFile(url, `BillingReport-${m.format('MM/YYYY')}.xlsx`);
    };

    const downloadUsage = async (type: ReportType, filePrefix: string) => {
        const range = ranges[type];
        if (!range) return;
        const url = await generateBillingReport({dateFrom: range.from.toISOString(), dateTo: range.to.toISOString(), timezone, type}).unwrap();
        saveFile(url, `${filePrefix}-${getFormattedDate(range.from)}-${getFormattedDate(range.to)}.xlsx`);
    };

    if (role === 'USER') return <AdminPanelRestrictedView/>;
    if (role !== 'ADMIN' && role !== 'SUPERADMIN') return null;

    return (
        <div className="w-full">
            <Section title="Billing report" description="Download a detailed billing summary for the selected month." isMobile={isMobile}>
                <Field label="Select a month to download">
                    <PhoneHQSelect
                        aria-label="Month"
                        className="w-[280px]"
                        value={month}
                        onChange={setMonth}
                        options={MONTH_OPTIONS}
                    />
                    <DownloadButton phqId="reports-downloadBillingBtn" label="Download billing report" onClick={downloadBilling}/>
                </Field>
            </Section>

            <Section title="Usage Report" description="Download a detailed report." isMobile={isMobile}>
                <div className="flex w-full flex-col">
                    {USAGE_REPORTS.map((report, i) => (
                        <div key={report.type} className={`flex flex-col gap-4 py-6 first:pt-0 last:pb-0 ${i > 0 ? 'border-0 border-t border-solid border-line' : ''}`}>
                            <div className="text-callout font-semibold text-ink">{report.title}</div>
                            <Field label="Specify a date range">
                                <PhoneHQDatepicker
                                    defaultOption="this-month"
                                    onDateConfirm={(dates, presetLabel) => {
                                        if (dates?.length !== 2) return;
                                        const range = {from: new Date(dates[0].valueOf()), to: new Date(dates[1].valueOf()), label: presetLabel};
                                        setRanges(prev => ({...prev, [report.type]: range}));
                                    }}
                                    trigger={<RangeTrigger range={ranges[report.type]}/>}
                                />
                                <DownloadButton
                                    phqId={report.phqId}
                                    label={`Download ${report.title.toLowerCase()}`}
                                    disabled={!ranges[report.type]}
                                    onClick={() => downloadUsage(report.type, report.filePrefix)}
                                />
                            </Field>
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
};

const Section = ({title, description, isMobile, children}: { title: string; description: string; isMobile: boolean; children: ReactNode }) => (
    <div className={`flex border-0 border-b border-solid border-line ${isMobile ? 'flex-col' : 'flex-row'}`}>
        <div className={`w-[344px] min-w-[300px] shrink-0 p-8 ${isMobile ? '' : 'border-0 border-r border-solid border-line'}`}>
            <p className="m-0 text-callout font-semibold text-ink">{title}</p>
            <p className="m-0 mt-1 text-footnote text-ink-muted">{description}</p>
        </div>
        <div className="flex min-w-0 flex-1 p-8">{children}</div>
    </div>
);

/** Label above, the control and its download button side by side. */
const Field = ({label, children}: { label: string; children: ReactNode }) => (
    <div className="flex flex-col gap-1.5">
        <div className="text-footnote font-medium text-ink-secondary">{label}</div>
        <div className="flex items-center gap-2">{children}</div>
    </div>
);

const DownloadButton = ({phqId, label, onClick, disabled}: { phqId: string; label: string; onClick: () => void; disabled?: boolean }) => (
    <PhoneHQButton phq-id={phqId} variant="secondary" size="icon" aria-label={label} title={disabled ? 'Pick a date range first' : label} disabled={disabled} onClick={onClick}>
        <Download size={18} aria-hidden="true"/>
    </PhoneHQButton>
);

/** Datepicker trigger. Popover.Trigger (asChild) hands it onClick, ref and aria props, so it must pass them on. */
const RangeTrigger = forwardRef<HTMLButtonElement, { range?: DateRange } & ButtonHTMLAttributes<HTMLButtonElement>>(({range, className, ...props}, ref) => (
    <button
        ref={ref}
        type="button"
        {...props}
        className={`box-border flex h-control-md w-[280px] cursor-pointer items-center gap-2 rounded-control border border-solid border-fill-subtle bg-surface px-3 text-left text-callout text-ink hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${className ?? ''}`}
    >
        <Calendar size={16} aria-hidden="true" className="shrink-0 text-ink-muted"/>
        <span className={`flex-1 truncate ${range ? '' : 'text-ink-muted'}`}>
            {!range ? 'Select dates' : range.label && range.label !== 'Custom' ? range.label : `${moment(range.from).format('D MMM YYYY')} – ${moment(range.to).format('D MMM YYYY')}`}
        </span>
    </button>
));
RangeTrigger.displayName = 'RangeTrigger';
